import { useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { isApiError } from '@platform/api-client';
import type { AdminIntegration } from '@platform/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Textarea,
  useToast,
} from '@platform/ui';

import { DataList, LoadingBlock } from '../../components/data';
import { TabPanel, Tabs } from '../../components/tabs';
import { useIntegrationsClient } from '../../lib/api/integrations-client-context';

import { ActivityLog } from './activity-log';
import { CredentialsForm } from './credentials-form';
import { CrmMappingForm } from './crm-mapping-form';
import { SettingsForm } from './settings-form';
import {
  CATEGORY_LABELS,
  formatLatency,
  formatMoment,
  HEALTH_RESULT_LABELS,
  HEALTH_RESULT_TONES,
  statusOf,
  STATUS_HINTS,
  STATUS_LABELS,
  STATUS_TONES,
} from './status';
import { StepUpPrompt, useStepUp } from './step-up-prompt';

/**
 * `/integrations/:key` — one integration, in full (MASTER_PLAN M3.9, FR-ADMIN-09).
 *
 * Five tabs, in the order an admin needs them: is it working, what are the keys, how is it
 * configured, what has it been doing, and how do I stop it. A CRM integration gets a sixth
 * — Marketing sync (M3.12) — between the settings and the activity log, because the CRM
 * mapping *is* configuration and belongs next to it rather than on a page of its own.
 *
 * ## Why this component fetches
 *
 * The rest of `apps/admin` keeps fetching in the route and screens purely presentational,
 * and the presentational pieces here follow that (`IntegrationsList`, `SchemaForm`). This
 * one does not, because the behaviours M3.9 has to get right — a refused call that opens a
 * step-up prompt and then re-runs itself, a probe that rewrites the install, a cursor page
 * that has to survive a filter change — are the wiring, and a test that cannot reach them
 * is testing the wrong thing. The client still arrives through a context, so a test injects
 * the mock and never needs a server or a router (rules §17.8, TESTING.md).
 */

export const DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'settings', label: 'Settings' },
  /**
   * M3.12, and the one tab that is not offered for every integration: a CRM mapping is
   * meaningless for Mailgun. It is in this list all the same so a bookmark to
   * `?tab=marketing` survives, and it is filtered out of the strip for a non-CRM
   * integration by `tabsFor` below.
   */
  { id: 'marketing', label: 'Marketing sync' },
  { id: 'activity', label: 'Activity' },
  { id: 'danger', label: 'Turn off' },
] as const;

export type DetailTabId = (typeof DETAIL_TABS)[number]['id'];

export function isDetailTab(value: string | undefined): value is DetailTabId {
  return DETAIL_TABS.some((tab) => tab.id === value);
}

/**
 * The tabs this integration really has.
 *
 * `marketing` is `crm` only. The category is the test rather than the key, so a second CRM
 * in the catalog gets the tab without a change here — and the tab's own screen still talks
 * to `/admin/integrations/highlevel/*`, which is the one CRM M3.12 syncs to.
 */
export function tabsFor(category: string): { id: DetailTabId; label: string }[] {
  return DETAIL_TABS.filter((tab) => tab.id !== 'marketing' || category === 'crm').map((tab) => ({
    id: tab.id,
    label: tab.label,
  }));
}

export interface IntegrationDetailProps {
  integrationKey: string;
  tab: DetailTabId;
  onTabChange: (tab: DetailTabId) => void;
  /** The router's Link back to the list, so this file never imports the router. */
  backLink: ReactNode;
}

export function IntegrationDetail({
  integrationKey,
  tab,
  onTabChange,
  backLink,
}: IntegrationDetailProps) {
  const client = useIntegrationsClient();
  const { toast } = useToast();
  const idBase = useId();
  const [probing, setProbing] = useState(false);

  const detail = useQuery({
    queryKey: ['integration', integrationKey],
    queryFn: () => client.get(integrationKey),
    retry: false,
  });

  const refresh = (): void => {
    void detail.refetch();
  };

  if (detail.isPending) {
    return (
      <>
        <PageHeader title="Integration" />
        <Card>
          <LoadingBlock label="Loading this integration" rows={4} />
        </Card>
      </>
    );
  }

  if (detail.isError) {
    const error = detail.error;
    return (
      <>
        <PageHeader title="Integration" actions={backLink} />
        <Alert
          tone={isApiError(error) && error.code === 'FEATURE_DISABLED' ? 'info' : 'error'}
          title={
            isApiError(error) && error.code === 'FEATURE_DISABLED'
              ? 'This integration is not enabled for your clinic.'
              : 'This integration could not be loaded.'
          }
        >
          {error instanceof Error ? error.message : 'Try again in a moment.'}
        </Alert>
      </>
    );
  }

  const entry: AdminIntegration = detail.data;
  const { integration, install } = entry;
  const status = statusOf(entry);
  const tabs = tabsFor(integration.category);
  /* A `?tab=marketing` bookmark on an integration that has no CRM opens the overview
     rather than an empty panel — the same forgiveness the route gives an unknown tab. */
  const active: DetailTabId = tabs.some((candidate) => candidate.id === tab) ? tab : 'overview';

  const runTest = async (): Promise<void> => {
    setProbing(true);
    try {
      const result = await client.test(integration.key);
      toast({
        tone: result.health.result === 'healthy' ? 'success' : 'warning',
        message:
          result.health.result === 'healthy'
            ? `The vendor answered in ${formatLatency(result.health.latencyMs)}.`
            : (result.health.message ?? 'The check did not pass. See the history below.'),
      });
      refresh();
    } catch (error) {
      toast({
        tone: 'error',
        message: isApiError(error) ? error.message : 'The check could not be run.',
      });
    } finally {
      setProbing(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={CATEGORY_LABELS[integration.category]}
        title={integration.name}
        description={
          integration.vendor === null
            ? (integration.description ?? undefined)
            : `${integration.vendor}${integration.description === null ? '' : ` · ${integration.description}`}`
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {backLink}
            <Button
              variant="secondary"
              disabled={probing || install === null || !install.credentialsSet}
              onClick={() => {
                void runTest();
              }}
            >
              {probing ? 'Testing…' : 'Test connection'}
            </Button>
          </div>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Badge tone={STATUS_TONES[status]}>{STATUS_LABELS[status]}</Badge>
        <span className="text-sm text-neutral-500">{STATUS_HINTS[status]}</span>
      </div>

      {status === 'not_enabled' ? (
        <Alert tone="info" title="Ask us to turn this on">
          Object Labs decides which integrations a clinic may use, and this one is not on for you
          yet. Contact support and we will enable it; you will then enter your own keys here.
        </Alert>
      ) : (
        <>
          <Tabs
            idBase={idBase}
            label="Integration sections"
            tabs={tabs}
            activeId={active}
            onChange={(next) => {
              if (isDetailTab(next)) onTabChange(next);
            }}
          />

          <TabPanel id="overview" idBase={idBase} active={active === 'overview'}>
            <Overview entry={entry} />
          </TabPanel>
          <TabPanel id="credentials" idBase={idBase} active={active === 'credentials'}>
            <CredentialsForm entry={entry} onChanged={refresh} />
          </TabPanel>
          <TabPanel id="settings" idBase={idBase} active={active === 'settings'}>
            <SettingsForm entry={entry} onChanged={refresh} />
          </TabPanel>
          <TabPanel id="marketing" idBase={idBase} active={active === 'marketing'}>
            <CrmMappingForm onChanged={refresh} />
          </TabPanel>
          <TabPanel id="activity" idBase={idBase} active={active === 'activity'}>
            <ActivityLog integrationKey={integration.key} />
          </TabPanel>
          <TabPanel id="danger" idBase={idBase} active={active === 'danger'}>
            <DangerZone entry={entry} onChanged={refresh} />
          </TabPanel>
        </>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- overview */

function Overview({ entry }: { entry: AdminIntegration }) {
  const client = useIntegrationsClient();
  const { integration, install } = entry;

  const history = useQuery({
    queryKey: ['integration-health', integration.key],
    queryFn: () => client.listHealth(integration.key, { limit: 10 }),
  });

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader as="h2" title="Last check" subtitle="What we saw the last time we looked." />
        <div className="px-6 pt-5 pb-6 max-[480px]:px-4">
          <DataList
            items={[
              { label: 'Checked', value: formatMoment(install?.lastHealthAt ?? null) },
              {
                label: 'Consecutive failures',
                value: String(install?.consecutiveFailures ?? 0),
              },
              {
                label: 'Last error',
                value:
                  install?.lastError == null ? (
                    <span className="text-neutral-400-text">None</span>
                  ) : (
                    <span className="text-danger-ink">{install.lastError}</span>
                  ),
              },
              {
                label: 'Adapter version',
                value: integration.version,
              },
            ]}
          />
        </div>
      </Card>

      {install?.webhookUrl == null ? null : <WebhookCard url={install.webhookUrl} />}

      <Card>
        <CardHeader
          as="h2"
          title="Check history"
          subtitle="Scheduled checks run every fifteen minutes."
          actions={
            integration.docsUrl === null ? undefined : (
              <a
                href={integration.docsUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-base font-medium text-primary-accessible hover:underline"
              >
                Vendor documentation
                <ExternalLink size={14} strokeWidth={2} aria-hidden={true} />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            )
          }
        />
        <div className="px-6 pt-5 pb-6 max-[480px]:px-4">
          {history.isPending ? (
            <LoadingBlock label="Loading the check history" rows={3} />
          ) : history.isError ? (
            <Alert tone="error">The check history could not be loaded.</Alert>
          ) : history.data.data.length === 0 ? (
            <EmptyState
              title="No checks yet"
              description="The first scheduled check runs within fifteen minutes of connecting."
            />
          ) : (
            <ul className="flex flex-col divide-y divide-neutral-100">
              {history.data.data.map((check) => (
                <li key={check.id} className="flex flex-wrap items-center gap-3 py-3">
                  <Badge tone={HEALTH_RESULT_TONES[check.result]}>
                    {HEALTH_RESULT_LABELS[check.result]}
                  </Badge>
                  <time dateTime={check.checkedAt} className="text-base text-ink tabular-nums">
                    {formatMoment(check.checkedAt)}
                  </time>
                  <span className="text-sm text-neutral-500">{formatLatency(check.latencyMs)}</span>
                  {check.message === null ? null : (
                    <span className="text-sm text-neutral-500">{check.message}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

function WebhookCard({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <Card>
      <CardHeader
        as="h2"
        title="Webhook address"
        subtitle="Paste this into the vendor's dashboard. Treat it like a password: anyone holding it can post events to your clinic."
      />
      <div className="flex flex-wrap items-end gap-3 px-6 pt-5 pb-6 max-[480px]:px-4">
        <Field label="Webhook URL" className="grow">
          <Input readOnly={true} value={url} />
        </Field>
        <Button
          variant="secondary"
          onClick={() => {
            void copy(url).then((ok) => {
              setCopied(ok);
            });
          }}
        >
          {copied ? (
            <Check size={16} strokeWidth={2} aria-hidden={true} />
          ) : (
            <Copy size={16} strokeWidth={2} aria-hidden={true} />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <span aria-live="polite" className="sr-only">
          {copied ? 'Webhook address copied to the clipboard.' : ''}
        </span>
      </div>
    </Card>
  );
}

/** The clipboard is unavailable over plain http and in jsdom; a failure is not a crash. */
async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------- danger zone */

function DangerZone({ entry, onChanged }: { entry: AdminIntegration; onChanged: () => void }) {
  const client = useIntegrationsClient();
  const { toast } = useToast();
  const stepUp = useStepUp();

  const { integration, install } = entry;
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [turningOff, setTurningOff] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [busy, setBusy] = useState(false);

  if (install === null) return null;

  const turnOff = async (): Promise<void> => {
    if (reason.trim().length < 3) {
      setReasonError('Say why, in a few words. It goes in the audit trail.');
      return;
    }
    setBusy(true);
    try {
      await stepUp.run('Turning this integration off', async () => {
        await client.disable(integration.key, reason.trim());
        toast({ tone: 'success', message: `${integration.name} is off. Dependent work pauses.` });
        setTurningOff(false);
        setReason('');
        onChanged();
      });
    } catch (error) {
      toast({
        tone: 'error',
        message: isApiError(error) ? error.message : 'That could not be turned off.',
      });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    setBusy(true);
    try {
      await stepUp.run('Removing the stored credentials', async () => {
        await client.deleteCredentials(integration.key);
        toast({ tone: 'success', message: 'The stored credentials were erased.' });
        setDisconnecting(false);
        onChanged();
      });
    } catch (error) {
      toast({
        tone: 'error',
        message: isApiError(error) ? error.message : 'The credentials could not be removed.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          as="h2"
          title="Turn off or disconnect"
          subtitle="Both need a code from your authenticator app, and both are recorded."
        />
        <div className="flex flex-col gap-5 px-6 pt-5 pb-6 max-[480px]:px-4">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-input border border-neutral-200 px-4 py-4">
            <div className="min-w-0">
              <p className="text-base font-semibold text-ink">Turn off</p>
              <p className="mt-1 text-sm text-neutral-500">
                Work that depends on this integration pauses. The keys stay stored, so turning it
                back on takes one click.
              </p>
            </div>
            {install.isEnabled ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setTurningOff(true);
                }}
              >
                Turn off
              </Button>
            ) : (
              <Badge tone="neutral">Already off</Badge>
            )}
          </div>

          <div className="flex flex-wrap items-start justify-between gap-4 rounded-input border border-danger-subtle px-4 py-4">
            <div className="min-w-0">
              <p className="text-base font-semibold text-ink">Remove credentials</p>
              <p className="mt-1 text-sm text-neutral-500">
                The stored keys are erased and this integration returns to not connected. You will
                need to paste them again from your vendor account.
              </p>
            </div>
            {install.credentialsSet ? (
              <Button
                variant="destructive"
                onClick={() => {
                  setDisconnecting(true);
                }}
              >
                Remove credentials
              </Button>
            ) : (
              <Badge tone="neutral">Nothing stored</Badge>
            )}
          </div>
        </div>
      </Card>

      <Dialog
        open={turningOff}
        onClose={() => {
          setTurningOff(false);
          setReasonError(undefined);
        }}
        title={`Turn off ${integration.name}?`}
        description="Anything waiting on this integration stops until it is turned back on."
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setTurningOff(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                void turnOff();
              }}
            >
              {busy ? 'Turning off…' : 'Turn it off'}
            </Button>
          </>
        }
      >
        <Field
          label="Why are you turning it off?"
          required={true}
          help="A few words. It is recorded in the audit trail."
          {...(reasonError === undefined ? {} : { error: reasonError })}
        >
          <Textarea
            rows={3}
            value={reason}
            disabled={busy}
            onChange={(event) => {
              setReason(event.currentTarget.value);
              setReasonError(undefined);
            }}
          />
        </Field>
      </Dialog>

      <Dialog
        open={disconnecting}
        onClose={() => {
          setDisconnecting(false);
        }}
        title="Remove the stored credentials?"
        description="They are erased, not disabled. Reconnecting means pasting them again from your vendor account."
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setDisconnecting(false);
              }}
            >
              Keep them
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                void disconnect();
              }}
            >
              {busy ? 'Removing…' : 'Remove them'}
            </Button>
          </>
        }
      >
        <p className="text-base text-neutral-700">
          {integration.name} stops working immediately and anything depending on it pauses.
        </p>
      </Dialog>

      <StepUpPrompt request={stepUp.pending} onClose={stepUp.cancel} />
    </>
  );
}
